import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Play, ArrowRight, CheckCircle, Users, Globe, Award,
  Sparkles, Heart, Shield, Target, Lightbulb, HandHeart,
  ChevronRight, TrendingUp, Clock, Zap, Quote, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/od8tpqbr_Nandhiji%20Interactions%20at%20Times%20now%20summit.mp4";
const PRODUCT_VIDEO_URL = "https://cdn.cness.io/Lndingpage_video.mp4";

// Real logos for Honouring Champions section
const HONORING_ORGS = [
  { name: "Adyar Ananda Bhavan (A2B)", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/yqt9guqz_A2B.png" },
  { name: "Bio-Dynamic Farming", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/xjxdei08_Bio%20farming.png" },
  { name: "Global Speakers Talk", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/qi8oocps_Global.png" },
  { name: "Prasanna International", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/4ik03p77_Prasana.png" },
  { name: "Rajan Eye Care Hospital", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/4g8wizeo_Rajan.png" },
  { name: "SF Enterprises & Engineering", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/j3zz3pgc_Frame%202121454824.png" },
  { name: "ib — Intelligent Building", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/uofm2pvr_Frame%202121454829.png" },
  { name: "Shriram Capital", logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/jy28wfmw_Frame%202121454824%20%281%29.png" },
];

// Real testimonials from cnessworks.com
const REAL_TESTIMONIALS = [
  {
    quote: "Our company is deeply rooted with value. It's not just a business built around numbers & profits but about giving back to the society in a very meaningful way. Our management goal is to do our best every day for our 17,000 employees, 10,000 suppliers and customers.",
    name: "Pooja Srinivasa Raja",
    role: "Executive Director, Adyar Ananda Bhavan",
    rating: 5,
    logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/yqt9guqz_A2B.png",
  },
  {
    quote: "Marketing is used to create awareness. If you create awareness, the idea of a conscious product can be sold. If you have a conscious idea, where you can contribute something good to society, success will follow.",
    name: "Sushant K Choudhury",
    role: "Executive Director, Shriram Capital Private Ltd",
    rating: 5,
    logo: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/jy28wfmw_Frame%202121454824%20%281%29.png",
  },
];

// Six Pillars Data
const SIX_PILLARS = [
  { id: 1, name: "Conscious Leadership", icon: Lightbulb, summary: "Build responsible decision-making, accountability and leadership practices.", color: "#3B82F6" },
  { id: 2, name: "Human-Centric Culture", icon: Users, summary: "Create a people-first workplace where teams feel valued and connected.", color: "#8B5CF6" },
  { id: 3, name: "Products & Services", icon: Target, summary: "Ensure offerings are useful, responsible and designed to create value.", color: "#10B981" },
  { id: 4, name: "Customers & Clients", icon: Heart, summary: "Strengthen trust, care, and long-term relationships with stakeholders.", color: "#F59E0B" },
  { id: 5, name: "Brand Vision & Collaboration", icon: Sparkles, summary: "Clarify purpose, voice, and collaborative role in the marketplace.", color: "#EC4899" },
  { id: 6, name: "Community & Social Impact", icon: HandHeart, summary: "Show how the company contributes beyond profit to society.", color: "#06B6D4" }
];

const TIERS = [
  { name: "Aspiring", description: "For organisations beginning their conscious business journey", features: ["Baseline assessment", "Quick wins", "Foundation building"], badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/tx2ugvu9_aspiring.jpeg" },
  { name: "Inspired", description: "For companies committed to implementing improvements", features: ["Detailed coaching", "Interim recognition", "Comprehensive evaluation"], badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/ko1qopap_inspired.jpeg" },
  { name: "Luminary", description: "Reserved for leaders who exemplify conscious business globally", features: ["Global recognition", "Thought-leadership", "Premium visibility"], badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/td0i3ea7_luminary.jpeg" }
];

const STATS = [
  { value: "97%", label: "Success Rate", icon: CheckCircle },
  { value: "30", label: "Days Average", icon: Clock },
  { value: "500+", label: "Champions", icon: Users },
  { value: "70%", label: "Gen Z Preference", icon: TrendingUp }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [productVideoPlaying, setProductVideoPlaying] = useState(false);

  return (
    <PublicLayout>
      <>
        {/* Hero Section — Split Layout with Video */}
        <section className="relative bg-gradient-to-br from-white via-[#FFFBEB] to-[#F8FAFC] overflow-hidden">
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-[#FDB813] rounded-full blur-3xl opacity-20" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1E3A5F] rounded-full blur-3xl opacity-20" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: copy */}
              <div className="text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-[#FDB813]" />
                  <span className="text-sm font-medium text-[#1E3A5F]">Conscious Enterprise Certification</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] leading-[1.1] mb-6">
                  The Conscious Unity
                  <span className="block text-[#FDB813]">for Humanity</span>
                </h1>

                <p className="text-xl text-gray-700 mb-4 font-light">
                  Amplify Your Brand. Build Trust. Scale Your Impact with CNESS.
                </p>

                <p className="text-base text-gray-600 max-w-xl mb-8 leading-relaxed">
                  Stakeholders expect more than purpose statements. They want evidence that your
                  organisation is ethical, responsible, sustainable and people-centred. CNESS turns
                  your responsible practices into visible, verifiable trust.
                </p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8">
                  <Button
                    onClick={() => navigate("/certification")}
                    className="px-8 py-6 text-base bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl shadow-lg"
                    data-testid="hero-cta-certification"
                  >
                    Start Your Certification Journey
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/enquiries")}
                    className="px-8 py-6 text-base border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
                    data-testid="hero-cta-enquiry"
                  >
                    Talk to Our Team
                  </Button>
                </div>

                {/* Trust micro-badges */}
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                  <span className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-1.5"/>500+ certified</span>
                  <span className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-1.5"/>30-day average</span>
                  <span className="flex items-center"><CheckCircle className="w-4 h-4 text-green-600 mr-1.5"/>Globally recognised</span>
                </div>
              </div>

              {/* Right: hero video */}
              <div className="relative">
                <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl bg-[#1E3A5F]" data-testid="hero-video-container">
                  {videoPlaying ? (
                    <video
                      src={HERO_VIDEO_URL}
                      controls
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                      data-testid="hero-video-player"
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <button
                      onClick={() => setVideoPlaying(true)}
                      data-testid="hero-video-play-btn"
                      className="group absolute inset-0 w-full h-full bg-gradient-to-br from-[#1E3A5F] via-[#152a45] to-[#0F172A] flex items-center justify-center text-white"
                    >
                      <video
                        src={HERO_VIDEO_URL}
                        muted
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/95 via-[#1E3A5F]/40 to-transparent" />
                      <div className="relative z-10 flex flex-col items-center text-center px-6">
                        <div className="w-20 h-20 bg-[#FDB813] rounded-full flex items-center justify-center shadow-2xl mb-4 group-hover:scale-110 transition-transform">
                          <Play className="w-9 h-9 text-[#1E3A5F] ml-1" fill="#1E3A5F" />
                        </div>
                        <p className="text-sm font-medium uppercase tracking-widest text-[#FDB813] mb-2">Watch the Vision</p>
                        <h3 className="text-xl sm:text-2xl font-bold leading-tight max-w-md">
                          Nandhiji at the Times Now Summit
                        </h3>
                        <p className="text-white/80 text-sm mt-2 max-w-md">
                          A conscious leader's call to embed responsibility into the DNA of business.
                        </p>
                      </div>
                    </button>
                  )}
                </div>

                {/* Floating stat card */}
                <div className="hidden md:flex absolute -left-6 -bottom-6 bg-white rounded-2xl shadow-xl p-4 items-center gap-3 border border-gray-100">
                  <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#FDB813]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1E3A5F] leading-none">97%</p>
                    <p className="text-xs text-gray-500">Certification success rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gradient-to-r from-[#1E3A5F] to-[#2563EB]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {STATS.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-8 h-8 text-[#FDB813] mx-auto mb-3" />
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Six Pillars Preview */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">The Six-Pillar Framework</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                CNESS certification evaluates organisations across six dimensions to make conscious business practical and measurable.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SIX_PILLARS.map((pillar) => (
                <div key={pillar.id} className="group p-6 bg-white border border-gray-100 rounded-2xl hover:shadow-xl hover:border-transparent transition-all">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${pillar.color}15` }}>
                    <pillar.icon className="w-7 h-7" style={{ color: pillar.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">{pillar.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{pillar.summary}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Button onClick={() => navigate('/framework')} variant="outline" className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-8 py-3 rounded-xl">
                Explore the Full Framework <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Certification Journey */}
        <section className="py-20 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">Your Certification Journey</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Certification is not just a badge; it is a structured system to help companies assess, align, advance and communicate better business practices.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                { step: "01", title: "Assess", desc: "Understand how your organisation currently performs across conscious enterprise practices." },
                { step: "02", title: "Align", desc: "Bring leadership, teams and operating practices around a shared framework." },
                { step: "03", title: "Advance", desc: "Use certification insights to prioritise improvements and show measurable progress." }
              ].map((item, index) => (
                <div key={index} className="relative bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-5xl font-bold text-[#FDB813]/30 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {TIERS.map((tier, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border-2 hover:border-[#FDB813] transition-all" style={{ borderColor: index === 2 ? '#FDB813' : '#E5E7EB' }}>
                  <img src={tier.badge} alt={`${tier.name} badge`} className="w-20 h-20 object-contain mb-4" />
                  <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">{tier.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{tier.description}</p>
                  <ul className="space-y-2">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Button onClick={() => navigate('/certification')} className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-3 rounded-xl">
                View Full Certification Details <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Why CNESS */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">Why CNESS Works</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                CNESS helps your organisation move from "we care" to "we can show how". 
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-4">Brand</h3>
                <p className="text-gray-600 mb-4">Position your company as a responsible, purpose-led business with certification that strengthens reputation.</p>
                <div className="bg-white p-4 rounded-xl border border-blue-100">
                  <p className="text-2xl font-bold text-blue-600">81%</p>
                  <p className="text-sm text-gray-500">of consumers must trust a brand before purchasing</p>
                </div>
              </div>
              
              <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-yellow-50 to-white border border-yellow-100">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-[#FDB813]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-4">Trust</h3>
                <p className="text-gray-600 mb-4">Give customers, employees, partners and investors a clear signal that your practices are credible.</p>
                <div className="bg-white p-4 rounded-xl border border-yellow-100">
                  <p className="text-2xl font-bold text-[#FDB813]">87%</p>
                  <p className="text-sm text-gray-500">of buyers pay more for brands they trust</p>
                </div>
              </div>
              
              <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-green-50 to-white border border-green-100">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Globe className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-4">Impact</h3>
                <p className="text-gray-600 mb-4">Show how your organisation creates positive value through better governance and practices.</p>
                <div className="bg-white p-4 rounded-xl border border-green-100">
                  <p className="text-2xl font-bold text-green-600">70%</p>
                  <p className="text-sm text-gray-500">of Gen Z seek values-aligned platforms</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Honoring Organizations - Client Logos Wall */}
        <section className="py-20 bg-white" data-testid="home-honoring-orgs">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                <Award className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm font-medium text-[#1E3A5F]">Honouring Champions</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-bold text-[#1E3A5F] mb-6 leading-tight">
                Honouring Organisations<br />
                That Already <span className="text-[#FDB813]">Lead With Impact</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Some organisations are established leaders in responsible and impactful business practices.
                CNESS certification recognises such organisations based on publicly available information,
                industry contributions, and demonstrated best practices.
              </p>
            </div>

            {/* Logo Grid - Real client logos */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 items-center max-w-5xl mx-auto">
              {HONORING_ORGS.map((org, i) => (
                <div
                  key={i}
                  className="aspect-[4/3] bg-white border border-gray-100 rounded-2xl p-6 flex flex-col items-center justify-center hover:shadow-xl hover:border-[#FDB813]/40 hover:-translate-y-1 transition-all group"
                  data-testid={`honoring-org-${i}`}
                >
                  <img
                    src={org.logo}
                    alt={org.name}
                    className="max-h-20 w-auto object-contain mb-2 group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                  <p className="text-[11px] sm:text-xs font-medium text-gray-500 text-center leading-tight">{org.name}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={() => navigate("/directory")}
                variant="outline"
                data-testid="home-view-directory-btn"
                className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-8 py-3 rounded-xl"
              >
                Explore Full Directory <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* World-Class "Your Journey to CNESS" Infographic */}
        <section className="py-20 bg-gradient-to-br from-[#1E3A5F] via-[#152a45] to-[#0F172A] text-white relative overflow-hidden" data-testid="home-journey-infographic">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, #FDB813 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#FDB813] rounded-full blur-[150px] opacity-10" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FDB813] rounded-full blur-[150px] opacity-10" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FDB813]/15 border border-[#FDB813]/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm font-medium text-[#FDB813]">The CNESS Journey</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                From <span className="text-[#FDB813]">curiosity</span> to <span className="text-[#FDB813]">certified</span><br />
                in 30 days.
              </h2>
              <p className="text-lg text-white/70">
                Four clear milestones. Independent verification. A badge that compounds trust every quarter.
              </p>
            </div>

            <div className="relative max-w-6xl mx-auto">
              {/* Connecting line — desktop only */}
              <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#FDB813]/30 to-transparent" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 relative">
                {[
                  { step: "01", icon: Lightbulb, title: "Discover", desc: "30-min discovery call. We map your stage, ambitions, and the right tier.", time: "Day 1" },
                  { step: "02", icon: Target, title: "Assess", desc: "Complete the 80-question assessment across all six pillars at your pace.", time: "Days 2–14" },
                  { step: "03", icon: Shield, title: "Verify", desc: "Independent evaluators score your evidence and conduct interviews.", time: "Days 15–25" },
                  { step: "04", icon: Award, title: "Celebrate", desc: "Receive your badge, directory listing, and Conscious Impact Score (CIS).", time: "Day 30" },
                ].map((s, i) => (
                  <div key={i} className="relative" data-testid={`journey-step-${i}`}>
                    {/* Step circle */}
                    <div className="relative z-10 flex justify-center mb-6">
                      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#FDB813] to-[#F59E0B] flex items-center justify-center shadow-2xl shadow-[#FDB813]/30 group hover:scale-105 transition-transform">
                        <div className="w-28 h-28 rounded-full bg-[#1E3A5F] flex flex-col items-center justify-center">
                          <s.icon className="w-8 h-8 text-[#FDB813] mb-1" />
                          <span className="text-xs font-bold text-white/70">{s.step}</span>
                        </div>
                      </div>
                    </div>
                    {/* Card */}
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#FDB813] mb-2">{s.time}</p>
                      <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                      <p className="text-sm text-white/70 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Outcome ribbon */}
            <div className="max-w-4xl mx-auto mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { v: "+34%", l: "Customer loyalty" },
                { v: "+28%", l: "Talent retention" },
                { v: "+22%", l: "Brand premium" },
                { v: "+19%", l: "Operational efficiency" },
              ].map((o, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
                  <div className="text-3xl font-black text-[#FDB813] leading-none">{o.v}</div>
                  <p className="text-xs text-white/60 mt-2">{o.l}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={() => navigate("/enquiries")}
                data-testid="home-journey-cta"
                className="bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-bold px-10 py-6 text-base rounded-xl shadow-lg"
              >
                Start My Journey <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Discover the CNESS Platform — split layout with product video */}
        <section className="py-20 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="home-platform-discovery">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left side: video */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#1E3A5F] via-[#152a45] to-[#0F172A]" data-testid="product-video-container">
                  {productVideoPlaying ? (
                    <video
                      src={PRODUCT_VIDEO_URL}
                      controls
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                      data-testid="product-video-player"
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <button
                      onClick={() => setProductVideoPlaying(true)}
                      data-testid="product-video-play-btn"
                      className="absolute inset-0 w-full h-full flex items-center justify-center group"
                    >
                      <video
                        src={PRODUCT_VIDEO_URL}
                        muted
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                      <div className="absolute inset-0 opacity-25" style={{
                        backgroundImage: "radial-gradient(circle at 25% 30%, #FDB813 1px, transparent 1px), radial-gradient(circle at 75% 70%, #FDB813 1px, transparent 1px)",
                        backgroundSize: "60px 60px",
                      }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/95 via-[#1E3A5F]/60 to-[#1E3A5F]/40" />
                      <div className="absolute top-6 left-6 opacity-50">
                        <svg width="44" height="44" viewBox="0 0 50 50">
                          <g stroke="#FDB813" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="25" y1="6" x2="25" y2="13"/>
                            <line x1="25" y1="37" x2="25" y2="44"/>
                            <line x1="6" y1="25" x2="13" y2="25"/>
                            <line x1="37" y1="25" x2="44" y2="25"/>
                            <line x1="11" y1="11" x2="16" y2="16"/>
                            <line x1="34" y1="34" x2="39" y2="39"/>
                            <line x1="11" y1="39" x2="16" y2="34"/>
                            <line x1="34" y1="16" x2="39" y2="11"/>
                          </g>
                          <circle cx="25" cy="25" r="9" fill="#FDB813"/>
                        </svg>
                      </div>
                      <div className="relative z-10 flex flex-col items-center text-center px-6 text-white">
                        <div className="w-20 h-20 bg-[#FDB813] rounded-full flex items-center justify-center shadow-2xl mb-4 group-hover:scale-110 transition-transform">
                          <Play className="w-9 h-9 text-[#1E3A5F] ml-1" fill="#1E3A5F" />
                        </div>
                        <p className="text-xs font-medium uppercase tracking-widest text-[#FDB813] mb-2">60-second product overview</p>
                        <h3 className="text-2xl font-bold leading-tight">Discover the CNESS Platform</h3>
                      </div>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">Tap to play · 60-second product overview</p>
              </div>

              {/* Middle: framed content */}
              <div className="lg:col-span-4 order-1 lg:order-2 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-[#FDB813]" />
                  <span className="text-sm font-medium text-[#1E3A5F]">Discover the CNESS Platform</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E3A5F] mb-6 leading-tight">
                  Brand. <span className="text-[#FDB813]">Trust.</span> Impact.<br/>For business.
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
                  See how CNESS enables organisations to build brand credibility, strengthen
                  stakeholder trust, and deliver measurable impact — all from one verified platform.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate("/gallery")}
                    variant="outline"
                    data-testid="platform-view-gallery-btn"
                    className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-6 py-3 rounded-xl"
                  >
                    View Gallery
                  </Button>
                  <Button
                    onClick={() => navigate("/certification")}
                    data-testid="platform-get-certified-btn"
                    className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-6 py-3 rounded-xl"
                  >
                    Get Certified <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Right side: stats / signals */}
              <div className="lg:col-span-3 order-3 grid grid-cols-2 lg:grid-cols-1 gap-3">
                {[
                  { value: "62%", label: "Consumers value aligned leadership" },
                  { value: "81%", label: "Trust drives buying decisions" },
                  { value: "90%", label: "Execs say sustainability is critical" },
                  { value: "73%", label: "Millennials prefer purpose-led brands" },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition">
                    <div className="text-2xl sm:text-3xl font-bold text-[#FDB813] leading-none">{s.value}</div>
                    <p className="text-xs text-gray-600 mt-1.5 leading-snug">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-[#F8FAFC]" data-testid="home-testimonials">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#FDB813]/30 rounded-full mb-6">
                <Star className="w-4 h-4 text-[#FDB813]" fill="#FDB813" />
                <span className="text-sm font-medium text-[#1E3A5F]">Voices from the Network</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
                What conscious leaders are saying
              </h2>
              <p className="text-lg text-gray-600">
                Trusted by founders, CEOs, and CHROs who are operationalising responsibility — not just talking about it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {REAL_TESTIMONIALS.map((t, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm hover:shadow-xl transition-all border border-gray-100 relative"
                  data-testid={`home-testimonial-${i}`}
                >
                  <div className="flex gap-1 mb-5">
                    {[...Array(t.rating)].map((_, idx) => (
                      <Star key={idx} className="w-5 h-5 text-[#FDB813]" fill="#FDB813" />
                    ))}
                  </div>
                  <Quote className="w-12 h-12 text-[#FDB813]/15 absolute top-6 right-6" />
                  <p className="text-gray-700 leading-relaxed mb-8 text-base relative z-10">"{t.quote}"</p>
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                    {t.logo ? (
                      <div className="w-16 h-16 bg-white border border-gray-100 rounded-xl flex items-center justify-center p-2 shadow-sm flex-shrink-0">
                        <img src={t.logo} alt={t.role} className="max-h-full max-w-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-[#FEF3C7] to-[#FDB813]/40 rounded-full flex items-center justify-center text-xl font-bold text-[#1E3A5F] shadow-sm">
                        {t.name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-[#1E3A5F]">{t.name}</p>
                      <p className="text-sm text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner Program Preview */}
        <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
                  <Zap className="w-4 h-4 text-[#FDB813]" />
                  <span className="text-sm font-medium text-white">Partner Program</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Join the CNESS Partner Network</h2>
                <p className="text-lg text-white/80 mb-8">
                  Entrepreneurs, consultants, media platforms, creators, and technology partners — 
                  discover mutually beneficial revenue opportunities through our five partnership pathways.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {["Referral & Client Partners", "Assessment Partners", "Media & Community Partners", "Affiliate & Influencer Partners"].map((partner, i) => (
                    <div key={i} className="flex items-center text-white/80">
                      <CheckCircle className="w-4 h-4 text-[#FDB813] mr-2 flex-shrink-0" />
                      <span className="text-sm">{partner}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => navigate('/partner-program')} className="bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold px-8 py-3 rounded-xl">
                  Explore Partnership Opportunities <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: "15%", label: "Revenue Share" },
                    { value: "5", label: "Pathways" },
                    { value: "Global", label: "Network" },
                    { value: "∞", label: "Potential" }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/10 rounded-2xl p-6 text-center">
                      <div className="text-3xl font-bold text-[#FDB813]">{stat.value}</div>
                      <div className="text-sm text-white/70">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#FEF3C7]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">Ready to Lead with Consciousness?</h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
              Join hundreds of forward-thinking organisations that have transformed their practices 
              and earned the trust of their stakeholders through CNESS certification.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={() => navigate('/certification')} className="w-full sm:w-auto px-8 py-4 text-lg bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl">
                Start Your Journey Today <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/contact')} className="w-full sm:w-auto px-8 py-4 text-lg border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl">
                Schedule a Discovery Call
              </Button>
            </div>
          </div>
        </section>
      </>
    </PublicLayout>
  );
};

export default HomePage;
